import React, { useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import EnvioAcessoPortalCard from './EnvioAcessoPortalCard';
import { Users, Loader2, Search, X } from 'lucide-react';

// Painel de contas do Portal do Consumidor. Duas partes: carga inicial (dispara
// provisionar-consumidores-em-lote) e a listagem das contas (consumidores +
// profiles.full_name) — de propósito NÃO fica junto de "Usuários e Permissões"
// (api.auth.listProfiles exclui role='consumidor' justamente pra essa tela não
// se misturar com staff). São 1000+ contas — a listagem nunca carrega a tabela
// inteira: mostra as 50 mais recentes do recorte atual e o total exato ao lado;
// pra achar uma conta específica se usa a busca ou os filtros.
//
// Os filtros vão pro banco, não pro array já carregado: filtrar em memória só
// olharia as 50 visíveis e daria resposta errada ("nenhuma conta com senha
// trocada") pro resto da base.

interface ResultadoLote {
    candidatos: number;
    criados: number;
    ja_existiam: number;
    falhas: number;
}

interface ContaConsumidor {
    id: string;
    cpf_cnpj: string;
    senha_provisoria_pendente: boolean;
    origem: 'manual' | 'carga_inicial' | 'automatico';
    criado_em: string;
    ultimo_acesso_em: string | null;
    profiles: { full_name: string } | null;
}

const COLUNAS = 'id, cpf_cnpj, senha_provisoria_pendente, origem, criado_em, ultimo_acesso_em';
const LIMITE = 50;

const ORIGEM_LABEL: Record<ContaConsumidor['origem'], string> = {
    manual: 'Manual',
    carga_inicial: 'Carga inicial',
    automatico: 'Automático',
};

type FiltroOrigem = 'todas' | ContaConsumidor['origem'];
type FiltroSenha = 'todas' | 'provisoria' | 'trocada';
type FiltroAcesso = 'todos' | 'entrou' | 'nunca';

const formatCpfCnpjExibicao = (digits: string): string => {
    if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return digits;
};

const dataHoraBr = (iso: string): string => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const SELECT_CLASS = 'py-2 pl-3 pr-8 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none';

const ConsumidoresPortalView: React.FC = () => {
    const { addToast } = useToast();
    const [processando, setProcessando] = useState(false);
    const [confirmarAberto, setConfirmarAberto] = useState(false);
    const [ultimoResultado, setUltimoResultado] = useState<ResultadoLote | null>(null);

    const [busca, setBusca] = useState('');
    const [filtroOrigem, setFiltroOrigem] = useState<FiltroOrigem>('todas');
    const [filtroSenha, setFiltroSenha] = useState<FiltroSenha>('todas');
    const [filtroAcesso, setFiltroAcesso] = useState<FiltroAcesso>('todos');

    const [contas, setContas] = useState<ContaConsumidor[]>([]);
    /** Quando cada conta visível recebeu o acesso por WhatsApp (#6138). Só envio real, não teste. */
    const [acessoEnviadoEm, setAcessoEnviadoEm] = useState<Record<string, string>>({});
    const [total, setTotal] = useState<number | null>(null);
    const [carregandoContas, setCarregandoContas] = useState(true);

    const temFiltro = filtroOrigem !== 'todas' || filtroSenha !== 'todas' || filtroAcesso !== 'todos';

    const limparFiltros = () => {
        setFiltroOrigem('todas');
        setFiltroSenha('todas');
        setFiltroAcesso('todos');
        setBusca('');
    };

    /** Aplica os três filtros em qualquer query — usado nas 3 consultas possíveis. */
    const comFiltros = (query: any) => {
        let q = query;
        if (filtroOrigem !== 'todas') q = q.eq('origem', filtroOrigem);
        if (filtroSenha === 'provisoria') q = q.eq('senha_provisoria_pendente', true);
        if (filtroSenha === 'trocada') q = q.eq('senha_provisoria_pendente', false);
        // ultimo_acesso_em nasce NULL: nunca entrou no portal.
        if (filtroAcesso === 'entrou') q = q.not('ultimo_acesso_em', 'is', null);
        if (filtroAcesso === 'nunca') q = q.is('ultimo_acesso_em', null);
        return q;
    };

    const carregarContas = async (termo: string) => {
        setCarregandoContas(true);
        try {
            if (!termo.trim()) {
                const { data, error, count } = await comFiltros(
                    supabase.from('consumidores').select(`${COLUNAS}, profiles(full_name)`, { count: 'exact' })
                )
                    .order('criado_em', { ascending: false })
                    .limit(LIMITE);
                if (error) throw error;
                setContas((data || []) as unknown as ContaConsumidor[]);
                setTotal(count ?? null);
                return;
            }

            const termoDigits = termo.replace(/\D/g, '');
            const [porNome, porCpf] = await Promise.all([
                comFiltros(supabase.from('consumidores').select(`${COLUNAS}, profiles!inner(full_name)`))
                    .ilike('profiles.full_name', `%${termo}%`)
                    .limit(LIMITE),
                termoDigits
                    ? comFiltros(supabase.from('consumidores').select(`${COLUNAS}, profiles(full_name)`))
                        .ilike('cpf_cnpj', `%${termoDigits}%`)
                        .limit(LIMITE)
                    : Promise.resolve({ data: [], error: null }),
            ]);
            if (porNome.error) throw porNome.error;
            if (porCpf.error) throw porCpf.error;
            const combinados = new Map<string, ContaConsumidor>();
            [...(porNome.data || []), ...(porCpf.data || [])].forEach((c) => combinados.set((c as ContaConsumidor).id, c as ContaConsumidor));
            const lista = Array.from(combinados.values());
            setContas(lista);
            setTotal(lista.length);
        } catch (err) {
            console.error('Erro ao carregar contas de consumidor:', err);
            addToast('Erro ao carregar as contas.', 'error');
        } finally {
            setCarregandoContas(false);
        }
    };

    // Busca os envios só das contas na tela — a lista nunca passa de LIMITE linhas.
    React.useEffect(() => {
        const ids = contas.map((c) => c.id);
        if (ids.length === 0) { setAcessoEnviadoEm({}); return; }
        let cancelado = false;
        supabase
            .from('portal_acesso_envios')
            .select('consumidor_id, enviado_em')
            .in('consumidor_id', ids)
            .eq('teste', false)
            .eq('status', 'enviado')
            .then(({ data, error }) => {
                if (cancelado) return;
                if (error) { console.error('Erro ao carregar envios do acesso:', error); return; }
                const mapa: Record<string, string> = {};
                (data || []).forEach((e: { consumidor_id: string; enviado_em: string }) => { mapa[e.consumidor_id] = e.enviado_em; });
                setAcessoEnviadoEm(mapa);
            });
        return () => { cancelado = true; };
    }, [contas]);

    React.useEffect(() => {
        const timeout = setTimeout(() => carregarContas(busca), 350);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busca, filtroOrigem, filtroSenha, filtroAcesso]);

    const rodarCargaInicial = async () => {
        setProcessando(true);
        try {
            const { data, error } = await supabase.functions.invoke('provisionar-consumidores-em-lote');
            if (error) throw error;
            const resultado = data?.data as ResultadoLote | undefined;
            if (resultado) {
                setUltimoResultado(resultado);
                addToast(`${resultado.criados} conta(s) nova(s) criada(s).`, 'success');
                await carregarContas(busca);
            }
        } catch (err) {
            console.error('Erro ao rodar carga inicial de consumidores:', err);
            addToast('Não foi possível rodar a carga inicial agora.', 'error');
        } finally {
            setProcessando(false);
            setConfirmarAberto(false);
        }
    };

    const buscando = !!busca.trim();
    const resumo = total === null
        ? ''
        : buscando
            ? `${total} encontrada(s)${total >= LIMITE ? ` — mostrando as ${LIMITE} primeiras` : ''}`
            : total > LIMITE
                ? `Mostrando as ${LIMITE} mais recentes de ${total.toLocaleString('pt-BR')} conta(s)`
                : `${total.toLocaleString('pt-BR')} conta(s)`;

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Users size={22} className="text-primary" />
                    Consumidores
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Contas de acesso ao Portal do Consumidor — cargo "Consumidor", separadas de Usuários e Permissões de propósito.
                </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
                <button
                    onClick={() => setConfirmarAberto(true)}
                    disabled={processando}
                    className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50"
                >
                    {processando && <Loader2 size={15} className="animate-spin" />}
                    Rodar carga inicial de acesso
                </button>
                <p className="text-xs text-gray-400 mt-2">
                    Todo CPF/CNPJ novo em Unidades Consumidoras já ganha conta sozinho. Este botão pega o que já existia antes disso — clique de novo a qualquer momento, ele só processa quem ainda não tem acesso.
                </p>

                {ultimoResultado && (
                    <div className="mt-4 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <p><strong className="text-gray-900 dark:text-white">{ultimoResultado.criados}</strong> conta(s) criada(s) agora.</p>
                        <p><strong className="text-gray-900 dark:text-white">{ultimoResultado.ja_existiam}</strong> já tinham acesso.</p>
                        {ultimoResultado.falhas > 0 && (
                            <p className="text-red-500"><strong>{ultimoResultado.falhas}</strong> falharam — veja os logs da function pra detalhes.</p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                            {ultimoResultado.candidatos} CPF/CNPJ distintos encontrados em Unidades Consumidoras no total.
                            {ultimoResultado.criados >= 100 && ' Chegou no teto de 100 desta rodada — clique de novo pra continuar.'}
                        </p>
                    </div>
                )}
            </div>

            <EnvioAcessoPortalCard />

            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 dark:border-gray-700 space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 min-w-[220px] max-w-sm">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                placeholder="Buscar por nome ou CPF/CNPJ..."
                                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                            />
                        </div>

                        <select value={filtroOrigem} onChange={(e) => setFiltroOrigem(e.target.value as FiltroOrigem)} className={SELECT_CLASS} aria-label="Filtrar por origem">
                            <option value="todas">Origem: todas</option>
                            <option value="carga_inicial">Carga inicial</option>
                            <option value="automatico">Automático</option>
                            <option value="manual">Manual</option>
                        </select>

                        <select value={filtroSenha} onChange={(e) => setFiltroSenha(e.target.value as FiltroSenha)} className={SELECT_CLASS} aria-label="Filtrar por senha">
                            <option value="todas">Senha: todas</option>
                            <option value="provisoria">Ainda provisória</option>
                            <option value="trocada">Já trocou</option>
                        </select>

                        <select value={filtroAcesso} onChange={(e) => setFiltroAcesso(e.target.value as FiltroAcesso)} className={SELECT_CLASS} aria-label="Filtrar por acesso">
                            <option value="todos">Acesso: todos</option>
                            <option value="entrou">Já entrou</option>
                            <option value="nunca">Nunca entrou</option>
                        </select>

                        {(temFiltro || buscando) && (
                            <button onClick={limparFiltros} className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-primary dark:text-gray-400 transition-colors">
                                <X size={14} /> Limpar
                            </button>
                        )}
                    </div>

                    {!carregandoContas && <p className="text-xs text-gray-400">{resumo}</p>}
                </div>

                {carregandoContas ? (
                    <div className="p-8 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
                        <Loader2 size={16} className="animate-spin" /> Carregando...
                    </div>
                ) : contas.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gray-400">Nenhuma conta encontrada com esse recorte.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-bold text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
                                    <th className="px-4 py-3">Nome</th>
                                    <th className="px-4 py-3">CPF/CNPJ</th>
                                    <th className="px-4 py-3">Cargo</th>
                                    <th className="px-4 py-3">Origem</th>
                                    <th className="px-4 py-3">Senha</th>
                                    <th className="px-4 py-3">Acesso enviado</th>
                                    <th className="px-4 py-3">Último acesso</th>
                                    <th className="px-4 py-3">Criado em</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {contas.map((c) => (
                                    <tr key={c.id}>
                                        <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{c.profiles?.full_name || '—'}</td>
                                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatCpfCnpjExibicao(c.cpf_cnpj)}</td>
                                        <td className="px-4 py-3">
                                            <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-bold">Consumidor</span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{ORIGEM_LABEL[c.origem]}</td>
                                        <td className="px-4 py-3">
                                            {c.senha_provisoria_pendente ? (
                                                <span className="text-amber-600 dark:text-amber-400 text-xs font-semibold">Provisória</span>
                                            ) : (
                                                <span className="text-gray-400 text-xs">Já trocou</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            {acessoEnviadoEm[c.id] ? (
                                                <span className="text-gray-600 dark:text-gray-300">{dataHoraBr(acessoEnviadoEm[c.id])}</span>
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            {c.ultimo_acesso_em ? (
                                                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{dataHoraBr(c.ultimo_acesso_em)}</span>
                                            ) : (
                                                <span className="text-gray-400">Nunca entrou</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-400 text-xs">{dataHoraBr(c.criado_em)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={confirmarAberto}
                onClose={() => setConfirmarAberto(false)}
                onConfirm={rodarCargaInicial}
                title="Rodar carga inicial de acesso ao portal?"
                description="Cria conta de consumidor (login = CPF/CNPJ, senha inicial = 6 primeiros dígitos) pra todo cliente de Unidades Consumidoras que ainda não tem acesso. Processa até 100 por vez — clique de novo se sobrar mais."
                confirmText="Rodar"
                variant="info"
                isLoading={processando}
            />
        </div>
    );
};

export default ConsumidoresPortalView;
